import { css, type SerializedStyles } from '@emotion/react'
import { accordionSummaryClasses, cardActionAreaClasses, formControlLabelClasses } from '@mui/material'
import { type Theme } from '@mui/material/styles'
import { rem, size } from 'polished'

import { type CommonCardBackground } from '@DS/components/components/foundations/shapes/card/types'
import { RADIO_PADDING_VALUE } from '@Material/radio'
import { RADIUS_1 } from '@Styles/constants/radius'
import { selectionAccessibilityBar } from '@Styles/selection'
import { remToNumberOfPixels } from '@Utils/units'

import type { RadioPlacement } from './BasicCardRadio.types'

export * from '../styles'

const BORDER_SIZE = rem(2)
const ICON_SIZE = rem(24)

export const basicCard = (
  theme: Theme,
  background: CommonCardBackground,
  backgroundColor: string | undefined,
  borderColor: string | undefined,
  isFullWidth: boolean | undefined,
): SerializedStyles =>
  css(
    {
      width: isFullWidth ? '100%' : 'auto',
    },
    borderColor && {
      boxShadow: `0 0 0 ${BORDER_SIZE} ${borderColor}`,
    },
    backgroundColor ? { backgroundColor } : { background: theme.customPalette.background[background] },
  )

export const withBottomChild = css({
  '&:hover': {
    borderRadius: `${RADIUS_1} ${RADIUS_1} 0 0`,
  },
})

export const basicCardButtonActionArea = css(size('100%'))

export const alertOrSelectedFocusVisibleStyle = (theme: Theme): SerializedStyles =>
  css({
    [`&.${cardActionAreaClasses.focusVisible}`]: {
      '&::before': {
        borderColor: theme.palette.common.white,
      },
    },
  })

export const selected = (theme: Theme, hasFocusVisible = false): SerializedStyles =>
  css(
    {
      '& svg': {
        fill: theme.palette.primary.contrastText,
      },
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
      color: theme.palette.primary.contrastText,
    },
    selectionAccessibilityBar(theme, hasFocusVisible),
  )

export const alert = (theme: Theme): SerializedStyles =>
  css({
    '& svg': {
      fill: theme.customPalette.onElements.elevated,
    },
    '&:hover': {
      backgroundColor: theme.palette.error.dark,
    },
    color: theme.customPalette.onElements.elevated,
  })

export const basicCardRadioContent = (radioPlacement: RadioPlacement, hasNoContentPadding = false): SerializedStyles =>
  css(
    {
      alignItems: 'center',
      cursor: 'pointer',
      display: 'flex',
      height: '100%',
      justifyContent: 'space-between',
    },
    radioPlacement === 'left' && {
      flexDirection: 'row-reverse',
    },
    hasNoContentPadding && {
      padding: 0,
    },
  )

export const basicCardRadioRadio = (theme: Theme, placement: RadioPlacement, spacingValue: number): SerializedStyles =>
  css({
    // TODO: https://jira.vsct.fr/browse/SIBR-3378
    margin: {
      left: `-${rem(RADIO_PADDING_VALUE)} ${rem(
        remToNumberOfPixels(theme.spacing(spacingValue)) - RADIO_PADDING_VALUE,
      )} -${rem(RADIO_PADDING_VALUE)} -${rem(RADIO_PADDING_VALUE)}`,
      right: `-${rem(RADIO_PADDING_VALUE)} -${rem(RADIO_PADDING_VALUE)} -${rem(RADIO_PADDING_VALUE)} ${rem(
        remToNumberOfPixels(theme.spacing(spacingValue)) - RADIO_PADDING_VALUE,
      )}`,
    }[placement],
  })

export const icon = (theme: Theme, isHovered: boolean, isFocused: boolean): SerializedStyles =>
  css(size(ICON_SIZE), {
    color: isHovered || isFocused ? theme.palette.primary.dark : theme.palette.primary.main,
    fill: 'currentcolor',
    flexShrink: 0,
  })

export const controlFormLabel = (theme: Theme): SerializedStyles =>
  css({
    [`& .${formControlLabelClasses.label}`]: {
      flex: 1,
      flexWrap: 'wrap',
      minWidth: 0,
    },
    display: 'flex',
    margin: 0,
    padding: theme.spacing(2),
  })

export const toggle = (theme: Theme): SerializedStyles =>
  css({
    margin: `-${theme.spacing(1)} -${theme.spacing(1)} -${theme.spacing(1)} 0`,
  })

export const basicAddExpandableCard = (theme: Theme): SerializedStyles =>
  css({
    [`.${accordionSummaryClasses.expandIconWrapper}`]: {
      paddingLeft: theme.spacing(1),
    },
    [`.${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]: {
      transform: 'none',
      transition: 'none',
    },
  })

export const basicAddExpandableCardDetails = (theme: Theme): SerializedStyles =>
  css({
    padding: theme.spacing(2),
  })

export const noPadding = css({
  ':last-child': {
    padding: 0,
  },
})
